export type SseEvent = {
    id: string | null;
    event: string;
    data: string;
};

// Reads a text/event-stream body until it ends. onActivity runs for every
// chunk, keepalive comments included, so a caller can detect a dead stream.
export async function readSse(
    body: ReadableStream<Uint8Array>,
    onEvent: (event: SseEvent) => void,
    onActivity: () => void = () => {},
): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let id: string | null = null;
    let event = '';
    let data: string[] = [];

    const handleLine = (line: string) => {
        if (line === '') {
            if (data.length !== 0) {
                onEvent({
                    id,
                    event: event || 'message',
                    data: data.join('\n'),
                });
            }
            event = '';
            data = [];
            return;
        }
        if (line.startsWith(':')) return;

        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? '' : line.slice(colon + 1);
        if (value.startsWith(' ')) value = value.slice(1);

        if (field === 'event') event = value;
        else if (field === 'data') data.push(value);
        else if (field === 'id' && !value.includes('\0')) id = value;
    };

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) return;
            onActivity();
            buffer += decoder.decode(value, { stream: true });
            // A trailing \r may be the first half of \r\n: keep it back.
            const held = buffer.endsWith('\r');
            const lines = (held ? buffer.slice(0, -1) : buffer).split(
                /\r\n|\r|\n/,
            );
            buffer = (lines.pop() ?? '') + (held ? '\r' : '');
            for (const line of lines) handleLine(line);
        }
    } finally {
        reader.releaseLock();
    }
}
